import { logger } from "@/modules/logger";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { SyncModelData, SyncOperationKind, OptimisticSyncUpdate } from "@/store/sync/types";
import { SessionModelData } from "@/store/sessions/types";
import { uuidModule } from "@/modules/uuid";
import { generateDefaultOwnerScopes } from "@/store/sync/operations/helpers/common";
import { IUpdateSessionOperation } from "@/store/sync/operations/types";

export class UpdateSessionOperation extends BaseSyncOperation<IUpdateSessionOperation> {
  get operationKind(): SyncOperationKind {
    return "UPDATE_SESSION";
  }

  public async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const optimisticUpdates: OptimisticSyncUpdate<SyncModelData>[] = [];
    const sessionUpdate = this.generateUpdateSessionUpdate();

    if (sessionUpdate) optimisticUpdates.push(sessionUpdate);

    return optimisticUpdates;
  }

  async triggerRecompute() {
    await this.store.sessions.recompute(this.payload.id);
  }

  protected generateUpdateSessionUpdate(): OptimisticSyncUpdate<SessionModelData> | null {
    const observable = this.store.sessions.get(this.payload.id);

    if (!observable) {
      logger.debug({
        message: "Attempted to update Session, but it was not found",
        info: { sessionId: this.payload.id },
      });

      return null;
    }

    const optimisticUpdate: OptimisticSyncUpdate<SessionModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: {
        model_id: this.payload.id,
        model_kind: "SESSION",
        model_version: 1,
        model_data: {
          id: this.payload.id,
          owned_by_space_account_id: observable.modelData.owned_by_space_account_id,
          status: this.payload.status as SessionModelData["status"],
          created_at: observable.modelData.created_at,
          title: this.payload.title,
          last_user_action_at: this.payload.last_user_action_at,
        },
        model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
      },
    };

    return optimisticUpdate;
  }
}
